import { type VariantProps, cva } from 'class-variance-authority'
import type { ButtonHTMLAttributes } from 'react'

import { cn } from '@/lib/cn'

import { Spinner } from './estado'

export const buttonVariants = cva(
  'press inline-flex items-center justify-center gap-3 rounded-2xl font-semibold select-none disabled:pointer-events-none disabled:opacity-40 aria-busy:pointer-events-none',
  {
    variants: {
      variant: {
        primary: 'bg-brand text-brand-fg hover:bg-brand-strong active:bg-brand-strong',
        secondary:
          'bg-elevated text-fg border border-border-strong hover:border-fg-subtle hover:bg-elevated/80 active:border-brand',
        ghost: 'text-fg-muted hover:bg-elevated hover:text-fg active:bg-elevated',
        verde: 'bg-verde text-verde-fg hover:brightness-110 active:brightness-95',
        amarelo: 'bg-amarelo text-amarelo-fg hover:brightness-110 active:brightness-95',
        vermelho: 'bg-vermelho text-vermelho-fg hover:brightness-110 active:brightness-95',
      },
      size: {
        sm: 'h-9 px-3 text-sm',
        md: 'h-11 px-4 text-base',
        /** Posto de trabalho: alvo ≥ 64px, texto grande. */
        tap: 'h-tap min-w-tap px-6 text-xl',
        /** Ação principal do posto (Iniciar/Concluir). */
        hero: 'h-tap-lg min-w-tap-lg px-8 text-2xl',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /**
   * Em andamento: mostra o spinner, mantém o tamanho e bloqueia o clique.
   * Prefira a `carregando` a trocar o texto por "Salvando…" à mão.
   */
  carregando?: boolean
  /**
   * Rótulo fixo e curto que nunca deve quebrar (cabeçalho do posto, barra de
   * título). Rótulo dinâmico (nome de posto, produto) fica sem isto: quebra
   * em vez de estourar a tela.
   */
  nowrap?: boolean
}

export function Button({
  className,
  variant,
  size,
  type = 'button',
  carregando = false,
  nowrap = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(buttonVariants({ variant, size }), nowrap && 'whitespace-nowrap', className)}
      disabled={carregando || disabled}
      aria-busy={carregando || undefined}
      {...props}
    >
      {carregando && (
        <Spinner className={size === 'hero' ? 'size-6' : size === 'tap' ? 'size-5' : 'size-4'} />
      )}
      {children}
    </button>
  )
}
